import { css, useTheme } from '@emotion/react';
import { useCallback, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { fontSizes, radii, spacing } from '../theme/tokens';

import { createPlan, fetchPlans, updatePlan } from '@/lib/api-client';
import type { Plan, PlanInput } from '@/lib/api-client';

interface PlanFormData {
  name: string;
  displayName: string;
  maxProjects: string;
  maxMonthlyMinutes: string;
  maxScriptDurationMinutes: string;
  maxSectionsPerProject: string;
  generationRateLimitPerHour: string;
  queuePriorityBoost: string;
  stripePriceId: string;
  isDefault: boolean;
  isActive: boolean;
}

const emptyForm: PlanFormData = {
  name: '',
  displayName: '',
  maxProjects: '',
  maxMonthlyMinutes: '',
  maxScriptDurationMinutes: '',
  maxSectionsPerProject: '',
  generationRateLimitPerHour: '5',
  queuePriorityBoost: '0',
  stripePriceId: '',
  isDefault: false,
  isActive: true,
};

function planToForm(plan: Plan): PlanFormData {
  return {
    name: plan.name,
    displayName: plan.displayName,
    maxProjects: plan.maxProjects?.toString() ?? '',
    maxMonthlyMinutes: plan.maxMonthlyMinutes ?? '',
    maxScriptDurationMinutes: plan.maxScriptDurationMinutes ?? '',
    maxSectionsPerProject: plan.maxSectionsPerProject?.toString() ?? '',
    generationRateLimitPerHour: plan.generationRateLimitPerHour.toString(),
    queuePriorityBoost: plan.queuePriorityBoost.toString(),
    stripePriceId: plan.stripePriceId ?? '',
    isDefault: plan.isDefault,
    isActive: plan.isActive,
  };
}

function formToInput(form: PlanFormData): PlanInput {
  return {
    name: form.name,
    displayName: form.displayName,
    maxProjects: form.maxProjects ? parseInt(form.maxProjects, 10) : null,
    maxMonthlyMinutes: form.maxMonthlyMinutes || null,
    maxScriptDurationMinutes: form.maxScriptDurationMinutes || null,
    maxSectionsPerProject: form.maxSectionsPerProject
      ? parseInt(form.maxSectionsPerProject, 10)
      : null,
    generationRateLimitPerHour: parseInt(form.generationRateLimitPerHour, 10) || 5,
    queuePriorityBoost: parseInt(form.queuePriorityBoost, 10) || 0,
    stripePriceId: form.stripePriceId || null,
    isDefault: form.isDefault,
    isActive: form.isActive,
  };
}

function formatLimit(value: number | string | null): string {
  if (value === null || value === undefined) return 'Unlimited';
  return value.toString();
}

export function AdminPlans() {
  const theme = useTheme();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState<PlanFormData>(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchPlans();
      setPlans(result);
    } catch {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  function handleEdit(plan: Plan) {
    setEditingId(plan.id);
    setIsAdding(false);
    setFormData(planToForm(plan));
  }

  function handleAdd() {
    setIsAdding(true);
    setEditingId(null);
    setFormData(emptyForm);
  }

  function handleCancel() {
    setEditingId(null);
    setIsAdding(false);
    setFormData(emptyForm);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const input = formToInput(formData);
      if (editingId) {
        const updated = await updatePlan(editingId, input);
        setPlans((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      } else {
        const created = await createPlan(input);
        setPlans((prev) => [...prev, created]);
      }
      handleCancel();
    } catch {
      setError('Failed to save plan');
    } finally {
      setSaving(false);
    }
  }

  function updateField(field: keyof PlanFormData, value: string | boolean) {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }

  const showForm = isAdding || editingId !== null;

  return (
    <div
      css={css`
        padding: ${spacing.lg};
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: ${spacing.lg};
        `}
      >
        <h1
          css={css`
            margin: 0;
            font-size: ${fontSizes.xxl};
            color: ${theme.colors.text};
          `}
        >
          Manage Plans
        </h1>
        {!showForm && (
          <Button variant="primary" shape="rect" onClick={handleAdd}>
            Add Plan
          </Button>
        )}
      </div>

      {error && (
        <p
          css={css`
            color: ${theme.colors.error};
            margin-bottom: ${spacing.md};
          `}
        >
          {error}
        </p>
      )}

      {loading && <p>Loading plans...</p>}

      {showForm && (
        <div
          css={css`
            border: 1px solid ${theme.colors.border};
            border-radius: 8px;
            padding: ${spacing.lg};
            margin-bottom: ${spacing.lg};
          `}
        >
          <h2>{editingId ? 'Edit Plan' : 'New Plan'}</h2>
          <div
            css={css`
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: ${spacing.md};
              margin-top: ${spacing.md};
            `}
          >
            <label>
              Name
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                disabled={editingId !== null}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Display Name
              <input
                type="text"
                value={formData.displayName}
                onChange={(e) => updateField('displayName', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Max Projects
              <input
                type="text"
                value={formData.maxProjects}
                placeholder="Unlimited"
                onChange={(e) => updateField('maxProjects', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Max Monthly Minutes
              <input
                type="text"
                value={formData.maxMonthlyMinutes}
                placeholder="Unlimited"
                onChange={(e) => updateField('maxMonthlyMinutes', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Max Script Duration (min)
              <input
                type="text"
                value={formData.maxScriptDurationMinutes}
                placeholder="Unlimited"
                onChange={(e) => updateField('maxScriptDurationMinutes', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Max Sections Per Project
              <input
                type="text"
                value={formData.maxSectionsPerProject}
                placeholder="Unlimited"
                onChange={(e) => updateField('maxSectionsPerProject', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Rate Limit (per hour)
              <input
                type="number"
                value={formData.generationRateLimitPerHour}
                onChange={(e) => updateField('generationRateLimitPerHour', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
            <label>
              Queue Priority Boost
              <input
                type="number"
                value={formData.queuePriorityBoost}
                onChange={(e) => updateField('queuePriorityBoost', e.target.value)}
                css={css`
                  display: block;
                  width: 100%;
                  margin-top: ${spacing.xs};
                  padding: ${spacing.sm} ${spacing.md};
                  background: ${theme.colors.surface};
                  color: ${theme.colors.text};
                  border: 1px solid ${theme.colors.border};
                  border-radius: ${radii.sm};
                  font-size: ${fontSizes.md};
                  box-sizing: border-box;
                  &:focus {
                    outline: none;
                    border-color: ${theme.colors.primary};
                  }
                `}
              />
            </label>
          </div>
          <div
            css={css`
              display: flex;
              gap: ${spacing.md};
              margin-top: ${spacing.lg};
            `}
          >
            <Button variant="primary" shape="rect" onClick={handleSave} disabled={saving}>
              Save
            </Button>
            <Button variant="secondary" shape="rect" onClick={handleCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!loading && plans.length > 0 && (
        <table
          css={css`
            width: 100%;
            border-collapse: collapse;

            th,
            td {
              padding: ${spacing.sm} ${spacing.md};
              text-align: left;
              border-bottom: 1px solid ${theme.colors.border};
            }

            th {
              font-weight: 600;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
              background: ${theme.colors.surface};
            }

            tbody tr:hover {
              background: ${theme.colors.surface};
            }

            tbody tr:nth-of-type(even) {
              background: ${theme.colors.surface}80;
            }

            tbody tr:nth-of-type(even):hover {
              background: ${theme.colors.surface};
            }
          `}
        >
          <thead>
            <tr>
              <th>Name</th>
              <th>Display Name</th>
              <th>Max Projects</th>
              <th>Monthly Min</th>
              <th>Rate Limit</th>
              <th>Priority</th>
              <th>Active</th>
              <th>Default</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr key={plan.id}>
                <td>{plan.name}</td>
                <td>{plan.displayName}</td>
                <td>{formatLimit(plan.maxProjects)}</td>
                <td>{formatLimit(plan.maxMonthlyMinutes)}</td>
                <td>{plan.generationRateLimitPerHour}/hr</td>
                <td>{plan.queuePriorityBoost}</td>
                <td>{plan.isActive ? 'Yes' : 'No'}</td>
                <td>{plan.isDefault ? 'Yes' : 'No'}</td>
                <td>
                  <Button variant="ghost" shape="rect" size="sm" onClick={() => handleEdit(plan)}>
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
